import { createSignal, createMemo, type Accessor } from 'solid-js';
import { produce, type SetStoreFunction } from 'solid-js/store';
import type { Canvas } from 'Shared/posters/project/posterSchema';
import type { Dimensions } from 'Shared/posters/types';

export interface CanvasContext {
	canvas: Accessor<Canvas>
	canvasBg: Accessor<string>
	setCanvasBg: (v: string) => void
	// Theme-variable bindings for canvas properties (currently just "background").
	canvasBindings: Accessor<Record<string, string>>
	// Binds one canvas property to a theme variable; an empty variable name clears the binding.
	// A key at a time rather than a whole map, because a map handed to a store is *merged* into
	// the one already there (mergeStoreNode writes only the keys it is given): a map with a key
	// removed left that binding standing, so recolouring a bound background wrote a colour the
	// still-bound theme variable went on overriding at render time.
	setCanvasBinding: (key: string, variable: string | undefined) => void
}

export function createCanvasState(
	canvasSlice: () => Canvas | undefined,
	setCanvas:   SetStoreFunction<Canvas> | undefined,
	dims:        () => Dimensions,
): CanvasContext {
	// Editable hosts pass the caller's live canvas slice plus a setter (writes go straight to the
	// caller's store); read-only hosts pass only a value and write nowhere, so their writes land in
	// local overrides instead. Reads follow the caller's slice in both cases until such an override
	// is set: seeding local state once would freeze the background of a long-lived viewer (the event
	// page's captured poster) against every later edit of the design it is showing.
	const [bgOverride, setBgOverride] = createSignal<string|undefined>()
	const [bindingsOverride, setBindingsOverride] = createSignal<Record<string, string>|undefined>()

	const canvasBg = setCanvas
		? () => canvasSlice()?.background ?? '#000'
		: () => bgOverride() ?? canvasSlice()?.background ?? '#000'
	const canvasBindings = setCanvas
		? () => canvasSlice()?.bindings ?? {}
		: () => bindingsOverride() ?? canvasSlice()?.bindings ?? {}
	const setCanvasBg = setCanvas ? (v: string) => setCanvas('background', v) : setBgOverride
	const setCanvasBinding = setCanvas
		? (key: string, variable: string | undefined) => setCanvas(produce((c: Canvas) => writeBinding(c, key, variable)))
		: (key: string, variable: string | undefined) => setBindingsOverride(withBinding(canvasBindings(), key, variable))

	// The owning theme's dimensions are authoritative: they override the design's own
	// width/height so the poster always renders at the theme's size — even as those
	// dimensions arrive asynchronously or change while the builder stays mounted.
	const canvas = createMemo<Canvas>(() => ({ ...canvasSlice(), ...dims() }))

	return { canvas, canvasBg, setCanvasBg, canvasBindings, setCanvasBinding }
}

// The map goes once it has no keys left, so a canvas whose last binding was cleared is written
// out exactly as one that was never bound.
function writeBinding(canvas: Canvas, key: string, variable: string | undefined): void
{
	const next = withBinding(canvas.bindings ?? {}, key, variable)
	if (Object.keys(next).length === 0)
		delete canvas.bindings
	else
		canvas.bindings = next
}

function withBinding(bindings: Record<string, string>, key: string, variable: string | undefined): Record<string, string>
{
	const next = {...bindings}
	if (variable == undefined || variable === '')
		delete next[key]
	else
		next[key] = variable
	return next
}

